import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Drawer from "./Drawer";
import Header from "./Header";
import { openDrawer } from "store/reducers/menu";
import { useDynamicBreakpoints } from "../../utils/breakpointUtils";

const REPORTING_BANNER_DISMISSED_KEY = "reportingSuiteBannerDismissed";
const REPORTING_BANNER_DISMISSED_TIMESTAMP_KEY = "reportingSuiteBannerDismissedTimestamp";

const MainLayout = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [reportingBannerDismissed, setReportingBannerDismissed] = useState(false);

    const user = JSON.parse(localStorage.getItem("user"));
    const isUser = user && user?.role === "";
    const isAdmin = user?.role === "Administrators" || user?.role?.includes("TenancyAdministrators");
    const { drawerOpen } = useSelector((state) => state.menu);
    const { isLoggedIn } = useSelector((state) => state.auth);
    const { tenancyId } = useSelector((state) => state.tenancy);

    useEffect(() => {
        const dismissed = localStorage.getItem(REPORTING_BANNER_DISMISSED_KEY);
        setReportingBannerDismissed(dismissed === "true");
    }, []);

    const handleReportingBannerDismiss = () => {
        localStorage.setItem(REPORTING_BANNER_DISMISSED_KEY, "true");
        localStorage.setItem(REPORTING_BANNER_DISMISSED_TIMESTAMP_KEY, Date.now().toString());
        setReportingBannerDismissed(true);
    };

    const brandType = process.env.REACT_APP_BRAND_TYPE;
    const showReportingBanner = isAdmin && !reportingBannerDismissed && brandType !== "censornet";

    const { matchDownSM, matchDownMD, matchDownLG } = useDynamicBreakpoints(drawerOpen);

    const handleDrawerToggle = () => {
        dispatch(openDrawer({ drawerOpen: !drawerOpen }));
    };

    useEffect(() => {
        // Only redirect if not logged in and not already navigating to login
        if (!isLoggedIn && location.pathname !== '/login') {
            navigate("/login");
        }
    }, [isLoggedIn, navigate, location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 800 && drawerOpen) {
                dispatch(openDrawer({ drawerOpen: false }));
            }
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [drawerOpen, dispatch]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", minHeight: "100vh" }}>
            <Box>
                <Header open={drawerOpen} />
            </Box>

            <Box sx={{ display: "flex", width: "100%", bgcolor: 'background.default', flex: 1, position: 'relative' }}>
                <Drawer open={drawerOpen} handleDrawerToggle={handleDrawerToggle} />

                <Box component="main" sx={{ width: drawerOpen ? "calc(100% - 260px)" : "100%", flexGrow: 1, p: { xs: 2, sm: 3 }, mt: -2 }}>
                    <Outlet />
                </Box>
            </Box>

            {showReportingBanner && (
                <Paper
                    elevation={3}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1500,
                        maxWidth: 640,
                        width: 'calc(100% - 48px)',
                        bgcolor: '#fff',
                        color: '#000',
                        fontFamily: '"Inter", sans-serif',
                        border: '1px solid rgba(0, 0, 0, 0.25)',
                        borderRadius: 2,
                        padding: '10px 12px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1
                    }}
                >
                    <NotificationsIcon sx={{ fontSize: 16, mt: '2px', color: '#000' }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', color: '#000', fontFamily: '"Inter", sans-serif', fontWeight: 500, lineHeight: 1.4, fontSize: '0.75rem' }}
                        >
                            Our new reporting suite is live! Our previous reporting suite has been retired on 5th October and all accounts have now moved to the new reporting suite. No action is needed.
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleReportingBannerDismiss}
                        size="small"
                        sx={{
                            color: '#000',
                            padding: '2px',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Paper>
            )}
        </Box>
    );
};

export default MainLayout;
